import { Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppConfig } from '../../app/app.config';
import { ProgressConfig } from '../../app/chatV2-view/helper/progress-config';

@Injectable({ providedIn: 'root' })
export class ChatProgressService {
  private readonly chatProgress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private readonly selectedChatType: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private internalProgress: number = 0;
  private processedQuestions: Set<string> = new Set();
  readonly botReplyInProgress = signal(false);

  constructor(private readonly appConfig: AppConfig) {}

  getChatProgressState(): BehaviorSubject<number> {
    return this.chatProgress;
  }

  setChatProgressState(progress: number): void {
    this.chatProgress.next(progress);
  }

  getSelectedChatType(): BehaviorSubject<string> {
    return this.selectedChatType;
  }

  setSelectedChatType(selectedChatType: string): void {
    this.selectedChatType.next(selectedChatType);
  }

  reset(): void {
    this.internalProgress = 0;
    this.processedQuestions = new Set();
    this.setChatProgressState(0);
  }

  /** Number of Anchor questions in ProgressConfig — the quiz step-counter total. */
  getAnchorQuestionCount(): number {
    return Object.values(ProgressConfig)
      .filter((item) => item.questionType === this.appConfig.Shared.Assistant.QuestionType.ANCHOR).length;
  }

  processChatBatch(chats: any[], concernClass: string): void {
    chats
      .filter((chat: any): boolean => chat.attributes.Owner === this.appConfig.Shared.Assistant.Owner.USER)
      .forEach((chat: any): void => this.processUserChat(chat, concernClass));

    chats
      .filter((chat: any): boolean => chat.attributes.Owner === this.appConfig.Shared.Assistant.Owner.BOT)
      .forEach((chat: any): void => this.processBotChat(chat));

    this.applyProgressFloor();
  }

  private processUserChat(chat: any, concernClass: string): void {
    const { questionId }: { questionId: string } = chat.attributes;
    const chatTypeKey: string = `${concernClass}-${questionId}`;

    if (this.shouldSkipUserChat(questionId, chatTypeKey)) return;

    this.processedQuestions.add(chatTypeKey);

    const configItem = Object.values(ProgressConfig).find(
      (item: any): boolean => item.questionId === questionId,
    );

    if (configItem) {
      this.applyConfigProgress(configItem);
    } else {
      this.applyDefaultProgress();
    }
  }

  private shouldSkipUserChat(questionId: string, chatTypeKey: string): boolean {
    if (this.internalProgress >= 90) return false;
    return questionId === null || questionId === undefined || this.processedQuestions.has(chatTypeKey);
  }

  private applyConfigProgress(configItem: any): void {
    if (configItem.questionType === this.appConfig.Shared.Assistant.QuestionType.ANCHOR) {
      this.applyAnchorProgress(configItem);
    } else if (configItem.questionType === this.appConfig.Shared.Assistant.QuestionType.INFO) {
      this.applyInfoProgress();
    }
  }

  private applyAnchorProgress(configItem: any): void {
    const progressValue = configItem.progressValue;
    const current = this.internalProgress;

    if (progressValue === null) {
      this.setProgress(Math.min(current + 5, 98));
      return;
    }

    if (progressValue > 98) {
      this.internalProgress = Math.min(current, 98);
      return;
    }

    if (progressValue > current && progressValue < 98) {
      this.setProgress(progressValue);
      return;
    }

    if (progressValue <= current) {
      this.setProgress(Math.min(current + 5, 98));
      return;
    }

    if (current >= 90 && current <= 98) {
      this.setProgress(Math.min(current + 2, 98));
    }
  }

  private applyInfoProgress(): void {
    this.setProgress(Math.min(this.internalProgress, 98));
  }

  private applyDefaultProgress(): void {
    if (this.internalProgress < 90) {
      this.setProgress(Math.min(this.internalProgress + 5, 98));
    } else if (this.internalProgress >= 90 && this.internalProgress < 98) {
      this.setProgress(Math.min(this.internalProgress + 2, 98));
    }
  }

  private processBotChat(chat: any): void {
    if (chat.attributes.uniqueId?.includes('regimen_delay')) {
      this.setProgress(Math.min(95, 98));
    }
  }

  private applyProgressFloor(): void {
    this.setProgress(Math.max(5, Math.min(this.internalProgress, 98)));
  }

  private setProgress(value: number): void {
    this.internalProgress = value;
    this.setChatProgressState(this.internalProgress);
  }
}
